import { useMemo } from 'react';
import ReactEChartsCore from 'echarts-for-react/lib/core';
import * as echarts from 'echarts/core';
import { BarChart } from 'echarts/charts';
import {
  GridComponent,
  LegendComponent,
  TooltipComponent,
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import { chartChromeColors } from '../../lib/chart-colors';
import { formatFaNumber } from '../../lib/utils';
import { IndexerIcon } from '../IndexerIcon';

echarts.use([
  BarChart,
  GridComponent,
  LegendComponent,
  TooltipComponent,
  CanvasRenderer,
]);

type Series = {
  key: string;
  label: string;
  color: string;
};

export function StackedBarChart({
  data,
  series,
  height = 280,
}: {
  data: Record<string, string | number>[];
  series: Series[];
  height?: number;
}) {
  const option = useMemo(() => {
    const chrome = chartChromeColors();
    return {
      textStyle: { fontFamily: 'dana' },
      tooltip: {
        trigger: 'axis' as const,
        axisPointer: { type: 'shadow' as const },
        padding: [7, 10],
        backgroundColor: chrome.tooltipBg,
        borderColor: chrome.tooltipBorder,
        borderWidth: 1,
        textStyle: { color: chrome.tooltipText },
        formatter: (params: { seriesName?: string; value?: number; color?: string; axisValueLabel?: string; name?: string }[]) => {
          if (!Array.isArray(params) || params.length === 0) return '';
          const title = params[0]?.axisValueLabel ?? params[0]?.name ?? '';
          const rows = params
            .map(
              (item) =>
                `${item.seriesName}: ${formatFaNumber(Number(item.value ?? 0))}`,
            )
            .join('<br/>');
          return `${title}<br/>${rows}`;
        },
      },
      legend: {
        bottom: 0,
        textStyle: { color: chrome.legend, fontSize: 12 },
      },
      grid: {
        top: 12,
        right: 12,
        left: 8,
        bottom: 36,
        containLabel: true,
      },
      xAxis: {
        type: 'category' as const,
        data: data.map((row) => String(row.label ?? '')),
        axisTick: { show: false },
        axisLine: {
          show: true,
          lineStyle: { color: chrome.line, type: 'solid' as const },
        },
        axisLabel: { color: chrome.label, fontSize: 11 },
      },
      yAxis: {
        type: 'value' as const,
        minInterval: 1,
        axisTick: { show: false },
        axisLine: { show: false },
        splitLine: {
          lineStyle: { type: 'dashed' as const, color: chrome.split },
        },
        axisLabel: { color: chrome.label, fontSize: 11 },
      },
      series: series.map((item, index) => ({
        name: item.label,
        type: 'bar' as const,
        stack: 'stack',
        barMaxWidth: 42,
        itemStyle: {
          color: item.color,
          borderRadius:
            index === series.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0],
        },
        data: data.map((row) => Number(row[item.key] ?? 0)),
      })),
    };
  }, [data, series]);

  if (data.length === 0 || series.length === 0) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary border border-dashed rounded-3 bg-body-tertiary" style={{ minHeight: height }}>
        <IndexerIcon name="bar-chart" />
        <span>داده‌ای برای نمایش نیست</span>
      </div>
    );
  }

  const hasValues = data.some((row) =>
    series.some((item) => Number(row[item.key] ?? 0) > 0),
  );
  if (!hasValues) {
    return (
      <div className="d-flex flex-column align-items-center justify-content-center gap-2 text-center text-secondary border border-dashed rounded-3 bg-body-tertiary" style={{ minHeight: height }}>
        <IndexerIcon name="bar-chart" />
        <span>در این بازه فعالیتی ثبت نشده</span>
      </div>
    );
  }

  return (
    <div style={{ height, direction: 'ltr' }}>
      <ReactEChartsCore
        echarts={echarts}
        option={option}
        style={{ height: '100%', width: '100%' }}
        opts={{ renderer: 'canvas' }}
      />
    </div>
  );
}
